/**
 * ComposerPlusMenu — the phone's one "bring something in" control (2026-09-17).
 *
 * Owner ruling, the same day the gutter placement shipped and was found broken:
 * on a phone, saved prompts and attach share a SINGLE '+' in the composer's
 * trailing cluster. `composerControlsLayout.ts` carries the whole reasoning; the
 * short version is that the cluster holds exactly two controls on a phone, and
 * the arrangement it replaces made the prompt library unreachable in precisely
 * the threads people use it in — any mention-gated group thread, and any draft
 * with a word already typed.
 *
 * Deliberately NOT a new overlay primitive. It is the same three-part portal
 * contract every other chat popup here obeys, and each part was written in a
 * production incident:
 *   1. portaled to `document.body` — the chat pane carries `backdrop-filter`
 *      and `container-type`, either of which makes it the containing block for
 *      a `position: fixed` child, so an in-tree popup is trapped at ANY z-index;
 *   2. `BODY_PORTAL_Z_INDEX` from the one ladder in `styles/layers`, never a
 *      literal — `check-overlay-layers` takes its denominator from every
 *      `createPortal` site under `src/`;
 *   3. `data-prest0n-portal="true"` on the scrim AND the surface, spelled with
 *      its VALUE, because the outside-click guard's selector is
 *      `[data-prest0n-portal="true"]` and an empty attribute silently never
 *      matched — which is how a scrim once closed the whole chat on every click.
 *
 * Placement goes through `dropUpPlacement` for the reason that module exists:
 * this thing hangs off a control near the right edge of the composer, which is
 * exactly where a surface placed with a left-edge-only clamp walks off screen.
 *
 * No Firebase import, by rule — a module pulled into the composer must not drag
 * `firebase/auth` (which touches global `fetch` at import time) into the
 * chat-isolation suites, which it took down before a single test ran.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BODY_PORTAL_Z_INDEX } from '../../styles/layers';
import { currentViewport, dropUpPlacement } from './dropUpPlacement';

export interface ComposerPlusMenuProps {
  open: boolean;
  /** The '+' button's rect, viewport-relative. */
  anchor: { left: number; top: number } | null;
  /** Menu labels, in the viewer's language. */
  labels: { heading: string; prompts: string; attach: string };
  /** Hidden entirely when attachments are switched off for this surface. */
  allowAttachments: boolean;
  /** A blocked wallet dims attach exactly as it dims the paperclip. */
  attachDisabled?: boolean;
  onPickPrompts: () => void;
  onPickAttach: () => void;
  onClose: () => void;
}

const MENU_WIDTH = 232;

const surfaceStyle: React.CSSProperties = {
  position: 'fixed',
  background: '#fbfaf6',
  color: '#3a3327',
  border: '1px solid rgba(198, 167, 94, 0.35)',
  borderRadius: 14,
  boxShadow: '0 6px 24px rgba(60, 48, 20, 0.14)',
  padding: '6px 0',
  zIndex: BODY_PORTAL_Z_INDEX,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
};

const itemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '11px 14px',
  border: 0,
  background: 'transparent',
  font: 'inherit',
  fontSize: 14,
  color: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  // 44px minimum target — this is a phone control.
  minHeight: 44,
};

export const ComposerPlusMenu: React.FC<ComposerPlusMenuProps> = ({
  open, anchor, labels, allowAttachments, attachDisabled, onPickPrompts, onPickAttach, onClose,
}) => {
  const surfaceRef = useRef<HTMLDivElement | null>(null);

  // Escape closes, and the key never reaches the composer behind it.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Focus the first item so the menu is operable by keyboard and announced.
  useEffect(() => {
    if (!open) return;
    const first = surfaceRef.current?.querySelector('button');
    (first as HTMLButtonElement | null)?.focus();
  }, [open]);

  const pick = useCallback((run: () => void) => {
    onClose();
    run();
  }, [onClose]);

  if (!open || !anchor || typeof document === 'undefined' || !document.body) return null;

  const placement = dropUpPlacement({
    anchor,
    viewport: currentViewport(),
    preferredWidth: MENU_WIDTH,
    minWidth: 180,
  });

  return createPortal(
    <>
      <div
        data-prest0n-portal="true"
        data-composer-plus-scrim=""
        style={{ position: 'fixed', inset: 0, zIndex: BODY_PORTAL_Z_INDEX, background: 'transparent' }}
        onMouseDown={onClose}
      />
      <div
        ref={surfaceRef}
        data-prest0n-portal="true"
        data-composer-plus-menu=""
        role="menu"
        aria-label={labels.heading}
        style={{
          ...surfaceStyle,
          left: placement.left,
          bottom: placement.bottom,
          width: placement.width,
        }}
      >
        <button
          type="button"
          role="menuitem"
          data-composer-plus-prompts=""
          style={itemStyle}
          onClick={() => pick(onPickPrompts)}
        >
          {labels.prompts}
        </button>
        {allowAttachments && (
          <button
            type="button"
            role="menuitem"
            data-composer-plus-attach=""
            disabled={attachDisabled}
            style={{ ...itemStyle, opacity: attachDisabled ? 0.5 : 1, cursor: attachDisabled ? 'not-allowed' : 'pointer' }}
            onClick={attachDisabled ? undefined : () => pick(onPickAttach)}
          >
            {labels.attach}
          </button>
        )}
      </div>
    </>,
    document.body,
  );
};

export default ComposerPlusMenu;
